import { Button, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import MultiLevelCheckbox from '/@/components/bayer/multi-level-checkbox';
import { useAdvisorRvInvitations, useCreateAdvisorRvInvitation, useDistributorsAndAdvisors } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { InvitedAdvisorInfo } from '.';
import { modalBodyPropsWide, modalProps } from '.';

const RVOnboardingModalAdvisorSelector = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
    const [selectedAdvisors, setSelectedAdvisors] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (open) {
            setSelectedAdvisors(new Set());
        }
    }, [open]);

    const distributorsAndAdvisorsQuery = useDistributorsAndAdvisors();
    const advisorsRvInvitationsQuery = useAdvisorRvInvitations();

    const distributorsAndAdvisors = useMemo(
        () =>
            (distributorsAndAdvisorsQuery.data?.results || []).map((el) => {
                return {
                    id: el.userNode.id,
                    label: el.userNode.name,
                    children: el.children.map((child) => {
                        const isLinked = child.isLinked;
                        const isInvited = advisorsRvInvitationsQuery.data?.results.some(
                            (invitation) =>
                                invitation.advisor.id === child.id &&
                                (invitation.status === 'pending' || invitation.status === 'accepted')
                        );

                        const doesntHasUser = child.user === null;

                        return {
                            id: child.id,
                            label: child.name,
                            disabled: doesntHasUser
                                ? _('No esta disponible')
                                : isLinked
                                  ? _('Ya está vinculado')
                                  : isInvited
                                    ? _('Ya fue invitado')
                                    : undefined,
                        };
                    }),
                };
            }),
        [distributorsAndAdvisorsQuery.data?.results, advisorsRvInvitationsQuery.data]
    );

    const createAdvisorRvInvitationMutation = useCreateAdvisorRvInvitation();

    const handleNext = useCallback(async () => {
        if (selectedAdvisors.size === 0) return;
        const advisors: InvitedAdvisorInfo[] = [];
        for (const dist of distributorsAndAdvisors) {
            for (const child of dist.children) {
                if (selectedAdvisors.has(child.id)) {
                    advisors.push({
                        id: child.id,
                        name: child.label,
                        distributor: dist.label,
                    });
                }
            }
        }
        if (advisors.length === 0) return;
        try {
            await Promise.all(
                advisors.map((advisor) => createAdvisorRvInvitationMutation.trigger({ userNodeId: advisor.id }))
            );

            setOpen(false);
            enqueueSnackbar(
                advisors.length === 1
                    ? _('Enviaste la solicitud de vinculación')
                    : _('Solicitudes de vinculación enviadas correctamente.'),
                { variant: 'success' }
            );
        } catch {
            enqueueSnackbar(_('Ocurrio un error, intenta nuevamente.'), { variant: 'error' });
        }
    }, [selectedAdvisors, distributorsAndAdvisors, setOpen, createAdvisorRvInvitationMutation]);

    return (
        <ModalV2
            open={open}
            onClose={() => setOpen(false)}
            title={_('Seleccioná a tus Asesores')}
            actions={
                <Button
                    variant='contained'
                    color='secondary'
                    onClick={handleNext}
                    disabled={selectedAdvisors.size === 0 || createAdvisorRvInvitationMutation.isMutating}
                >
                    {createAdvisorRvInvitationMutation.isMutating
                        ? _('Enviando...')
                        : _('Enviar solicitud de vinculación')}
                </Button>
            }
            {...modalProps}
            maxWidth='sm'
            bodyProps={{
                ...modalProps.bodyProps,
                ...modalBodyPropsWide,
                sx: {
                    height: '70vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Typography variant='body1' color='primary' sx={{ flexShrink: 0 }}>
                {_(
                    'Cuando acepten la solicitud, van a ver y trabajar con la información de los respectivos productores Premium que comparten.'
                )}
            </Typography>
            <MultiLevelCheckbox
                selectedItems={selectedAdvisors}
                setSelectedItems={setSelectedAdvisors}
                InputProps={{
                    placeholder: _('Buscar Distribuidor, nombre o apellido'),
                }}
                items={distributorsAndAdvisors}
                verbose={_('Asesor', { count: selectedAdvisors.size })}
                loading={distributorsAndAdvisorsQuery.isLoading}
                sx={{ flex: 1, minHeight: 0 }}
            />
        </ModalV2>
    );
};

export default RVOnboardingModalAdvisorSelector;
